import { DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, ViewChild, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import type { FileSelectEvent, FileUpload } from 'primeng/fileupload';
import { FileUploadModule } from 'primeng/fileupload';
import { PasswordModule } from 'primeng/password';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { NotificationService } from '../../../services/notification.service';
import { PdfCertificateApiService } from '../../services/pdf-certificate-api.service';
import { extractBackendErrorMessage } from '../../utils/extract-backend-error-message';
import { extractCertificateCommonName } from '../../utils/extract-certificate-common-name';
import { PdfCertificateStatus } from '../../models/pdf-signer.models';

const UPLOAD_ERROR_FALLBACK_MESSAGE =
  'Zertifikat konnte nicht hochgeladen werden. Bitte erneut versuchen.';
const DELETE_ERROR_FALLBACK_MESSAGE =
  'Zertifikat konnte nicht entfernt werden. Bitte erneut versuchen.';

/**
 * Dialog zur Selbstverwaltung des eigenen PFX-Signaturzertifikats: Status anzeigen,
 * hochladen/ersetzen und löschen. Ein Zertifikat pro Nutzer - ein neuer Upload ersetzt
 * das vorherige.
 */
@Component({
  selector: 'app-pdf-certificate-dialog',
  standalone: true,
  providers: [ConfirmationService],
  imports: [
    FormsModule,
    DatePipe,
    ButtonModule,
    FileUploadModule,
    PasswordModule,
    ConfirmDialogModule,
  ],
  templateUrl: './pdf-certificate-dialog.component.html',
  styleUrl: './pdf-certificate-dialog.component.scss',
})
export class PdfCertificateDialogComponent implements OnInit {
  private readonly ref = inject(DynamicDialogRef);
  private readonly certificateApi = inject(PdfCertificateApiService);
  private readonly notification = inject(NotificationService);
  private readonly confirmationService = inject(ConfirmationService);

  // kein { static: true }: #pfxUpload steckt im @else-Zweig von @if (loadingStatus()), das
  // beim ersten Rendern noch true ist (loadStatus() lädt asynchron) - eine statische Query
  // würde vor der Erstellung dieses eingebetteten Views aufgelöst und bliebe dauerhaft
  // undefined. Ohne die Ref läuft clear() in resetUploadForm() ins Leere - ein erneuter Klick
  // auf den Auswählen-Button (mode="basic") würde dann statt den Dateidialog erneut zu öffnen
  // upload() aufrufen (siehe PrimeNG FileUpload-Quelltext), was mangels url/customUpload ins
  // Leere läuft.
  @ViewChild('pfxUpload')
  private pfxUploadRef?: FileUpload;

  readonly loadingStatus = signal(true);
  readonly certificate = signal<PdfCertificateStatus | null>(null);
  readonly pfxFile = signal<File | null>(null);
  readonly uploading = signal(false);
  readonly deleting = signal(false);

  private readonly passwordSignal = signal('');

  get password(): string {
    return this.passwordSignal();
  }
  set password(value: string) {
    this.passwordSignal.set(value);
  }

  ngOnInit(): void {
    this.loadStatus();
  }

  // Der Distinguished Name ist lang und für sich genommen schwer lesbar - im Dialog steht
  // deshalb der Common Name voran, der vollständige DN darunter.
  commonName(subject: string): string {
    return extractCertificateCommonName(subject);
  }

  close(): void {
    this.ref.close();
  }

  onPfxFileSelected(event: FileSelectEvent): void {
    const file = event.files[0];
    if (!file) return;
    this.pfxFile.set(file);
    // Siehe Kommentar in PdfSignerPageComponent.onPdfFileSelected() - gleicher Grund.
    this.pfxUploadRef?.clear();
  }

  upload(): void {
    const file = this.pfxFile();
    const password = this.password;
    if (!file || !password) return;

    this.uploading.set(true);
    this.certificateApi.upload(file, password).subscribe({
      next: (status) => {
        this.uploading.set(false);
        this.certificate.set(status);
        this.resetUploadForm();
        this.notification.success('Zertifikat erfolgreich hochgeladen.');
      },
      error: (err: HttpErrorResponse) => {
        this.uploading.set(false);
        this.password = '';
        this.notification.error(
          this.extractErrorMessage(err, UPLOAD_ERROR_FALLBACK_MESSAGE),
        );
      },
    });
  }

  // Löscht das einzige hinterlegte Zertifikat unwiderruflich - ein Fehlklick soll das nicht
  // auslösen können, deshalb erst die Bestätigung einholen (gleiches Muster wie
  // InventoryComponent.confirmDelete()).
  delete(): void {
    this.confirmationService.confirm({
      message:
        'Soll das hinterlegte Zertifikat wirklich entfernt werden? Ohne Zertifikat können Sie keine Dokumente mehr signieren, bis ein neues hochgeladen wurde.',
      header: 'Löschbestätigung',
      icon: 'pi pi-exclamation-triangle',
      accept: () => this.performDelete(),
    });
  }

  private performDelete(): void {
    this.deleting.set(true);
    this.certificateApi.delete().subscribe({
      next: () => {
        this.deleting.set(false);
        this.certificate.set(null);
        this.notification.success('Zertifikat entfernt.');
      },
      error: (err: HttpErrorResponse) => {
        this.deleting.set(false);
        this.notification.error(
          this.extractErrorMessage(err, DELETE_ERROR_FALLBACK_MESSAGE),
        );
      },
    });
  }

  private loadStatus(): void {
    this.loadingStatus.set(true);
    this.certificateApi.getStatus().subscribe({
      next: (status) => {
        this.loadingStatus.set(false);
        this.certificate.set(status);
      },
      error: (err: HttpErrorResponse) => {
        this.loadingStatus.set(false);
        // 404 bedeutet lediglich "noch kein Zertifikat hinterlegt" - kein Fehlertoast.
        if (err.status === 404) {
          this.certificate.set(null);
          return;
        }
        this.notification.error(
          'Zertifikatsstatus konnte nicht geladen werden.',
        );
      },
    });
  }

  private resetUploadForm(): void {
    this.pfxFile.set(null);
    this.password = '';
    this.pfxUploadRef?.clear();
  }

  // Antworten der Zertifikat-Endpunkte sind JSON (kein Blob wie beim Signieren) -
  // err.error ist hier bereits das geparste Objekt.
  private extractErrorMessage(
    err: HttpErrorResponse,
    fallback: string,
  ): string {
    return extractBackendErrorMessage(err.error, fallback);
  }
}
